import type { VerificationRequest } from 'matrix-js-sdk/src/crypto-api';
import { CryptoEvent, VerificationPhase, VerificationRequestEvent } from 'matrix-js-sdk/src/crypto-api';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isLoginSecretStorageUnlockActive } from 'contexts/MatrixContext/loginSecretStorageUnlock';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import useMxCookies from 'modules/Authentication/hooks/useMxCookies';
import getDeviceLabel from 'utils/getDeviceLabel';

import { CrossSigningStatusEnum } from './types';

type RequestState = {
  request: VerificationRequest | null;
  changeHandler: (() => void) | null;
  lastPhase: VerificationPhase | null;
};

interface UseVerificationRequestProps {
  refreshCurrentDeviceStatus: () => void;
  crossSigningStatus: CrossSigningStatusEnum;
}
const useVerificationRequest = ({ refreshCurrentDeviceStatus, crossSigningStatus }: UseVerificationRequestProps) => {
  const { matrixClient, matrixClientRef } = useMatrixContext({ requireDefinedMatrixClient: false });
  const { mxUserId, mxDeviceId } = useMxCookies();
  const { pushToast } = useToast();

  const { t } = useTranslation();
  const [activeRequest, setActiveRequest] = useState<VerificationRequest | null>(null);
  const [hasIncomingRequest, setHasIncomingRequest] = useState(false);
  const [hasCompletedSelfVerification, setHasCompletedSelfVerification] = useState(false);

  const initRequestState = useMemo(() => ({ request: null, changeHandler: null, lastPhase: null }), []);

  // This holds whatever verification request is currently active
  // When a request comes in, we store it here along with its listener fn
  // We need for acceptRequest() and cancelRequest() so they know which request to act on
  // Also when a new request comes in, we use this to clean up the old listener before attaching a new one
  // Resets values to null when the request finishes or gets cancelled
  const requestStateRef = useRef<RequestState>(initRequestState);

  // This is just a list of requests we've already seen
  // Matrix SDK sometimes sends us the same request multiple times
  // If we don't track what we've already handled, we'd add listeners multiple times to the same thing
  const processedRequestsRef = useRef<WeakSet<VerificationRequest>>(new WeakSet());

  // Remote key reset on another device invalidates this one — allow the verification picker to show again.
  useEffect(() => {
    if (crossSigningStatus === CrossSigningStatusEnum.NOT_VERIFIED) {
      setHasCompletedSelfVerification(false);
    }
  }, [crossSigningStatus]);

  const isIncomingRequest = useCallback(
    ({ phase, initiatedByMe }: VerificationRequest) =>
      phase !== VerificationPhase.Done && phase !== VerificationPhase.Cancelled && !initiatedByMe,
    []
  );

  const resetRequestState = useCallback(() => {
    const state = requestStateRef.current;
    if (state.request && state.changeHandler) state.request.off(VerificationRequestEvent.Change, state.changeHandler);

    requestStateRef.current = initRequestState;
    setActiveRequest(null);
    setHasIncomingRequest(false);
  }, [initRequestState]);

  const attachRequestListeners = useCallback(
    (request: VerificationRequest) => {
      const state = requestStateRef.current;
      if (state.request === request && state.changeHandler) return;

      const onChange = () => {
        const currentState = requestStateRef.current;
        const isSamePhase = currentState.lastPhase === request.phase;
        const isSameRequest = currentState.request === request;
        if (isSamePhase && isSameRequest) return;

        const previousPhase = currentState.lastPhase;
        currentState.lastPhase = request.phase;

        if (
          request.phase === VerificationPhase.Cancelled &&
          previousPhase !== VerificationPhase.Cancelled &&
          currentState.request === request
        ) {
          // Since we can not know request timeout correctly because sdk so we can't show timer,
          // and we can't know if request was cancelled or timed out, we display this
          pushToast({
            variant: 'info',
            title: t('verification.verification_timed_out_or_cancelled'),
            autoDismiss: true,
          });
          resetRequestState();
          refreshCurrentDeviceStatus();
          return;
        }

        const isRequestDone = request.phase === VerificationPhase.Done;
        if (isRequestDone && currentState.request === request) {
          if (request.isSelfVerification && request.initiatedByMe) {
            setHasCompletedSelfVerification(true);
            pushToast({
              variant: 'success',
              title: t('verification.device_successfully_verified'),
            });
          }

          resetRequestState();
          return;
        }

        const isIncoming = isIncomingRequest(request);
        setActiveRequest(request);
        setHasIncomingRequest(isIncoming);
      };

      if (state.changeHandler && state.request) state.request.off(VerificationRequestEvent.Change, state.changeHandler);

      request.on(VerificationRequestEvent.Change, onChange);
      requestStateRef.current = { request, changeHandler: onChange, lastPhase: request.phase };

      const isIncoming = isIncomingRequest(request);
      setActiveRequest(request);
      setHasIncomingRequest(isIncoming);
    },
    [isIncomingRequest, resetRequestState, pushToast, t, refreshCurrentDeviceStatus]
  );

  const handleVerificationRequest = useCallback(
    (request: VerificationRequest) => {
      if (!request.isSelfVerification || processedRequestsRef.current.has(request)) return;
      if (
        (request.phase === VerificationPhase.Done || request.phase === VerificationPhase.Cancelled) &&
        requestStateRef.current.request === request
      ) {
        return resetRequestState();
      }

      processedRequestsRef.current.add(request);
      attachRequestListeners(request);
    },
    [attachRequestListeners, resetRequestState]
  );

  const requestVerification = useCallback(async () => {
    // During login unlock the context client is still null; fall back to the ref client so the
    // request is sent before the gate resolves.
    const client = matrixClient ?? (isLoginSecretStorageUnlockActive() ? matrixClientRef.current : null);
    if (!client || !mxUserId || !mxDeviceId) return null;

    const crypto = client.getCrypto();
    if (!crypto) return null;
    await client.setDeviceDetails(mxDeviceId, { display_name: getDeviceLabel() });

    const request = await crypto.requestOwnUserVerification();

    handleVerificationRequest(request);

    return request;
  }, [handleVerificationRequest, matrixClient, matrixClientRef, mxUserId, mxDeviceId]);

  const acceptRequest = useCallback(async () => {
    const request = requestStateRef.current.request;
    if (request) await request.accept();
  }, []);

  const cancelRequest = useCallback(() => {
    const request = requestStateRef.current.request;
    if (request) request.cancel();
    resetRequestState();
  }, [resetRequestState]);

  useEffect(() => {
    // During login unlock the context client is still null; use the ref client to subscribe to
    // incoming verification requests arriving before the gate resolves.
    const client = matrixClient ?? (isLoginSecretStorageUnlockActive() ? matrixClientRef.current : null);
    if (!client) return resetRequestState();

    const crypto = client.getCrypto();
    if (!crypto) return;

    client.on(CryptoEvent.VerificationRequestReceived, handleVerificationRequest);

    if (mxUserId && !requestStateRef.current.request) {
      const existingRequests = crypto.getVerificationRequestsToDeviceInProgress(mxUserId);
      if (existingRequests.length) {
        const validRequest = existingRequests.find(
          (req) => req.isSelfVerification && ![VerificationPhase.Done, VerificationPhase.Cancelled].includes(req.phase)
        );
        if (validRequest) handleVerificationRequest(validRequest);
      }
    }

    return () => {
      client.off(CryptoEvent.VerificationRequestReceived, handleVerificationRequest);
      const state = requestStateRef.current;
      if (state.request && state.changeHandler) state.request.off(VerificationRequestEvent.Change, state.changeHandler);
      requestStateRef.current = initRequestState;
    };
  }, [
    handleVerificationRequest,
    initRequestState,
    matrixClient,
    matrixClientRef,
    mxUserId,
    resetRequestState,
    crossSigningStatus,
  ]);

  return {
    activeRequest,
    hasIncomingRequest,
    hasCompletedSelfVerification,
    requestVerification,
    acceptRequest,
    cancelRequest,
    resetRequestState,
  };
};

export default useVerificationRequest;
